import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import UserApi from "api/userApi";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import CollapseFilter from "components/Common/CollapseFilter";
import DatePickerCustom from "components/Common/DatePickerCustom";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import ModalPreview from "components/Common/ModalPreview";
import {
  LIST_APPLICATION_STATUS,
  OPTION_EXPERIENCE,
  LIST_OPTION_GENDER,
  LIST_OPTION_SALARY_TYPE,
  COLOR_APPLICATION_STATUS2,
  ENUM_EXPERIENCE_USER,
  COLOR_EXPERIENCE,
} from "helpers/constans";
import { formatDateStringToKorea, formatDateStringToUTC, formatNumber } from "helpers/format";
import CountUp from "react-countup";
import { getUsers as onGetUsers } from "store/user/thunk";
import Detail from "./Detail";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import { getApplications } from "store/application/thunk";
import NO_IMAGE_USER from "../../assets/images/no-user.png";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import CopyWrapper from "components/Common/CopyWrapper";
import noImageJob from "../../assets/images/no-job-post.png";
import ModalMultiPreviewImage from "components/Common/ModalMultiPreviewImage";
import { NumericFormat } from 'react-number-format';
import DropdownAsyncUsers from "components/Common/DropdownAsyncUsers";
import NO_IMAGE_JOB from "../../assets/images/no-job-post.png";


registerLocale("en", en);
registerLocale("ko", ko);

const ApplicationManagement = () => {
  const { t, i18n } = useTranslation();
  const dispatch: any = useDispatch();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    jobTitle: withDefault(StringParam, ""),
    recruiterUserId: withDefault(StringParam, ""),
    fullname: withDefault(StringParam, ""),
    email: withDefault(StringParam, ""),
    phoneNumber: withDefault(StringParam, ""),
    introduce: withDefault(StringParam, ""),
    experience: withDefault(StringParam, ""),
    region: withDefault(StringParam, ""),
    gender: withDefault(StringParam, ""),
    salaryType: withDefault(StringParam, ""),
    salaryFrom: withDefault(StringParam, ""),
    salaryTo: withDefault(StringParam, ""),
    status: withDefault(StringParam, ""),
    startDate: withDefault(StringParam, ""),
    endDate: withDefault(StringParam, ""),
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [addressSearch, setAddressSearch] = useState<string>(
    query?.address || ""
  );
  const [itemToEdit, setItemToEdit] = useState<any>(false);

  const [nickNameSearch, setNicknameSearch] = useState<string>(
    query?.fullname || ""
  );

  const [regionSearch, setRegionSearch] = useState<string>(query?.region || "");
  const [userIdSearch, setUserIdSearch] = useState<any>(query?.recruiterUserId || "");

  const [phoneNumberSearch, setPhoneNumberSearch] = useState<string>(
    query?.phoneNumber || ""
  );

  const [salaryFromSearch, setSalaryFromSearch] = useState<string>(
    query?.salaryFrom || ""
  );

  const [salaryToSearch, setSalaryToSearch] = useState<string>(
    query?.salaryTo || ""
  );

  const [jobTitleSearch, setJobTitleSearch] = useState<string>(
    query?.jobTitle || ""
  );
  const [introduce, setIntroduce] = useState<string>(query?.introduce || "");

  const [emailSearch, setEmailSearch] = useState<string>(query?.email || "");

  const OPTION_GENDER = LIST_OPTION_GENDER.filter((i) => i?.value !== "OTHER");

  const [genderSearch, setGenderSearch] = useState<any>(
    OPTION_GENDER?.find((item) => item?.value === query?.gender)?.value ?? ""
  );

  const OPTION_SALARY_TYPE = LIST_OPTION_SALARY_TYPE.concat([
    { label: "Negotiable", value: "NEGOTIABLE" },
  ]);

  const [salaryTypeSearch, setSalaryTypeSearch] = useState<any>(
    OPTION_SALARY_TYPE?.find((item) => item?.value === query?.gender)?.value ??
    ""
  );

  const [statusSearch, setStatusSearch] = useState<any>(
    LIST_APPLICATION_STATUS?.find((item) => item?.value === query?.status)
      ?.value ?? ""
  );

  const [experienceSearch, setExperienceSearch] = useState<any>(
    OPTION_EXPERIENCE?.find((item) => item?.value === query?.experience)
      ?.value ?? ""
  );

  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.startDate
      ? moment(query?.startDate || "", "Y-MM-DD").toDate()
      : null,
    query?.endDate ? moment(query?.endDate || "", "Y-MM-DD").toDate() : null,
  ]);
  const [startDate, endDate] = dateSearch;

  const [linkImage, setLinkImage] = useState<any>(null);

  const selectLayoutState = (state: any) => state.Application;
  const ApplicationProperties = createSelector(selectLayoutState, (state) => ({
    application: state?.Applications,
    isApplicationLoading: state?.isApplicationLoading,
    isApplicationSuccess: state?.isApplicationSuccess,

    error: state.error,
  }));

  // Inside your component
  const { application, isApplicationLoading } = useSelector(
    ApplicationProperties
  );

  useEffect(() => {
    dispatch(getApplications(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      fullname: encodeURIComponent(nickNameSearch.trim()) || "",
      recruiterUserId: userIdSearch?.value !== "all" ? userIdSearch?.value : "",
      email: encodeURIComponent(emailSearch.trim()) || "",
      jobTitle: encodeURIComponent(jobTitleSearch.trim()) || "",
      phoneNumber: phoneNumberSearch,
      introduce: encodeURIComponent(introduce.trim()) || "",
      experience: experienceSearch,
      region: regionSearch,
      gender: genderSearch,
      salaryType: salaryTypeSearch,
      salaryFrom: salaryFromSearch,
      salaryTo: salaryToSearch,
      status: statusSearch,
      startDate: startDate
        ? formatDateStringToUTC(
          moment(new Date(startDate))
            .startOf("day")
            .format("Y-MM-DD HH:mm:ss")
        )
        : "",
      endDate: endDate
        ? formatDateStringToUTC(
          moment(new Date(endDate)).endOf("day").format("Y-MM-DD HH:mm:ss")
        )
        : "",

      page: 1,
      time_request: +new Date(),
      address: addressSearch.trim() || "",
    });
  };

  const resetData = () => {
    setQuery(
      {
        jobTitle: "",
        recruiterUserId: "",
        fullname: "",
        email: "",
        phoneNumber: "",
        introduce: "",
        experience: "",
        region: "",
        gender: "",
        salaryType: "",
        salaryFrom: "",
        salaryTo: "",
        status: "",
        startDate: "",
        endDate: "",
        sortBy: "",
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setJobTitleSearch("");
    setUserIdSearch("");
    setNicknameSearch("");
    setEmailSearch("");
    setPhoneNumberSearch("");
    setIntroduce("");
    setRegionSearch("");
    setExperienceSearch("");
    setSalaryTypeSearch("");
    setSalaryFromSearch("");
    setSalaryToSearch("");
    setStatusSearch("");
    setDateSearch([null, null]);
  };

  // Begin::Update User
  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    dispatch(onGetUsers(query));
  };
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div style={{ width: 35 }}>
              {application?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },

      {
        Header: t("Application Status"),
        accessor: "status",
        thWidth: i18n?.language === 'en' ? 150 : 120,
        filterable: false,
        sortable: false,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_APPLICATION_STATUS2[cell?.value] || "secondary"
                  }`}
              >
                {t(
                  LIST_APPLICATION_STATUS?.find(
                    (item: any) => String(item?.value) === String(cell?.value)
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Job Title"),
        accessor: "jobPost.title",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 320,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          
          return cell?.value ? (
            <div 
              className="cursor-pointer text-primary"
              onClick={() => {
                setIsOpen(true);
                setItemToEdit(item);
              }}
              >
                <CopyWrapper contentCopy={cell?.value} style={{ minWidth: 300 }}>
                  {`${cell?.value}`}
                </CopyWrapper>
              </div>
            
          ) : "-";
        },
      },
      {
        Header: t("Job Thumbnail"),
        accessor: "jobPost.jobAttachment",
        thClass: "text-center whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 120,
        Cell: (cell: any) => {
          const itemImages = cell?.value || [];
          const jobImage = itemImages?.[0]?.url || "";
          const formatPreviewImages = itemImages?.length > 0 && itemImages?.map((image: any) => (image?.url));

          return <>
            <div className="w-100 text-center " style={{ minWidth: "80px" }}>
              <div
                style={{ width: "80px" }}
                className="m-auto cursor-pointer"
                onClick={() => setLinkImage(itemImages?.length > 1 ? { urls: formatPreviewImages } : { url: jobImage })}
              // onClick={() => setLinkImage(jobImage ?? noImageJob)}
              >
                <img
                  src={jobImage ?? noImageJob}
                  width={70}
                  height={70}
                  style={{ objectFit: "contain" }}
                  className=""
                  alt="thumbnail"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = noImageJob;
                  }}
                />
              </div>
            </div>
          </>
        },
      },
      {
        Header: t("Store Thumbnail"),
        accessor: "jobPost.store.thumbnailUrl",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap text-center",
        thWidth: 180,
        Cell: (cell: any) => {
          const storeImage = cell?.value;

          return <>
            <div className="w-100 text-center " style={{ minWidth: "80px" }}>
              <div
                style={{ width: "80px" }}
                className="m-auto cursor-pointer"
                onClick={() => setLinkImage({ url: storeImage })}
              >
                <img
                  src={storeImage ?? noImageJob}
                  width={70}
                  height={70}
                  style={{ objectFit: "contain" }}
                  className=""
                  alt="thumbnail"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = noImageJob;
                  }}
                />
              </div>
            </div>
          </>
        },
      },
      {
        Header: t("Store Name"),
        accessor: "jobPost.store.name",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 160,
        Cell: (cell: any) => {
          return cell?.value ? (
            <CopyWrapper contentCopy={cell?.value} style={{ minWidth: 160 }}>
              {`${cell?.value}`}
            </CopyWrapper>
          ) : "-";
        },
      },
      {
        Header: t("Job Start Date"),
        accessor: "jobPost.displayStartDate",
        filterable: true,
        sortable: false,
        thWidth: 140,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Job End Date"),
        accessor: "jobPost.displayEndDate",
        filterable: true,
        sortable: false,
        thWidth: 140,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Owner Post"),
        accessor: "Store",
        filterable: false,
        sortable: false,
        thWidth: 320,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const owner = cell?.row?.original?.owner;
          return owner ? (
            <div
              className="d-flex align-items-center gap-2"
            >
              <div
                className="cursor-pointer overflow-hidden object-fit-cover"
                style={{
                  height: "65px",
                  width: "65px",
                  minWidth: "65px",
                }}
                onClick={() =>
                  setLinkImage({ url: owner?.storeThumbnail || "" })
                }
              >
                <img
                  className="overflow-hidden object-fit-cover"
                  src={owner?.storeThumbnail || NO_IMAGE_JOB}
                  alt={owner?.storeName}
                  height={65}
                  width={65}
                  style={{
                    minWidth: "65px",
                  }}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = NO_IMAGE_JOB;
                  }}
                />
              </div>

              <div className="d-flex flex-column" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                <div className="fw-semibold">
                  <CopyWrapper contentCopy={owner?.email || ""}>
                    {owner?.email || ""}
                  </CopyWrapper>
                </div>
                <div>
                  <CopyWrapper contentCopy={owner?.storeName || ""}>
                    {owner?.storeName || ""}
                  </CopyWrapper>
                </div>
                <small className="text-secondary">
                  <CopyWrapper contentCopy={owner?.businessNumber || ""}>
                    {owner?.businessNumber || ""}
                  </CopyWrapper>
                </small>
                <small className="text-secondary">
                  <CopyWrapper contentCopy={owner?.storeShortAddress || ""}>
                    {owner?.storeShortAddress || ""}
                  </CopyWrapper>
                </small>
              </div>
            </div>
          ) : "-";
        },
      },
      {
        Header: t("User"),
        accessor: "JobSeeker",
        filterable: false,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          const images = item?.imageUrls;
          const fullname = item?.fullname;
          const email = item?.email;
          const phoneNumber = item?.phoneNumber;

          return (
            <div
              style={{ width: "230px" }}
              className="d-flex align-items-center gap-2"
            >
              <div
                className="cursor-pointer overflow-hidden object-fit-cover"
                style={{
                  height: "45px",
                  width: "45px",
                  minWidth: "45px",
                }}
                onClick={() => setLinkImage(images?.length > 1 ? { urls: images } : { url: images?.[0] || "" })}
              >
                <img
                  className="overflow-hidden object-fit-cover rounded-circle"
                  src={images?.[0] || NO_IMAGE_USER}
                  alt={fullname}
                  height={45}
                  width={45}
                  style={{
                    minWidth: "45px",
                  }}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = NO_IMAGE_USER;
                  }}
                />
              </div>
              {fullname ? (
                <div>
                  <div className="fw-semibold">
                    {fullname || ""} -{" "}
                    <small className="fw-medium text-secondary">
                      {phoneNumber || ""}
                    </small>
                  </div>
                  <small className="text-secondary">
                    {email || ""}
                  </small>
                </div>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },

      {
        Header: t("Gender"),
        accessor: "gender",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 100,
        Cell: (cell: any) => {
          const gender = cell?.value;
          return (
            <div style={{ minWidth: 80 }}>
              {t(
                LIST_OPTION_GENDER?.find(
                  (item: any) => String(item?.value) === String(gender)
                )?.label || ""
              )}
            </div>
          );
        },
      },

      // {
      //   Header: t("Avatar"),
      //   accessor: "imageUrls",
      //   filterable: true,
      //   sortable: false,
      //   thClass: "whitespace-nowrap text-center",
      //   thWidth: 100,
      //   Cell: (cell: any) => {
      //     const images = cell?.value;
      //     return (
      //       <div className="w-100 text-center " style={{ minWidth: "60px" }}>
      //         <div
      //           style={{ width: "45px" }}
      //           className="m-auto cursor-pointer"
      //           onClick={() => setLinkImage(images?.length > 1 ? { urls: images } : { url: images?.[0] || "" })}
      //         >
      //           <img
      //             src={images?.[0] || avatar1}
      //             className="rounded-circle avatar-sm"
      //             alt="thumbnail"
      //             onError={({ currentTarget }) => {
      //               currentTarget.onerror = null; // prevents looping
      //               currentTarget.src = avatar1;
      //             }}
      //           />
      //         </div>
      //       </div>
      //     );
      //   },
      // },
      // {
      //   Header: t("Full Name"),
      //   accessor: "fullname",
      //   filterable: true,
      //   sortable: false,
      //   thClass: "whitespace-nowrap",
      //   thWidth: 180,
      //   Cell: (cell: any) => {
      //     return cell?.value ? (
      //       <CopyWrapper contentCopy={cell?.value} style={{minWidth: 160}}>
      //         {`${cell?.value}`}
      //       </CopyWrapper>
      //     ) : "-";
      //   },
      // },
      // {
      //   Header: t("Email"),
      //   accessor: "email",
      //   filterable: true,
      //   sortable: false,
      //   thClass: "whitespace-nowrap",
      //   thWidth: 180,
      //   Cell: (cell: any) => {
      //     return cell?.value ? (
      //       <CopyWrapper contentCopy={cell?.value} style={{minWidth: 160}}>
      //         {`${cell?.value}`}
      //       </CopyWrapper>
      //     ) : "-";
      //   },
      // },
      // {
      //   Header: t("Phone Number"),
      //   accessor: "phoneNumber",
      //   filterable: true,
      //   sortable: false,
      //   thClass: "whitespace-nowrap",
      //   thWidth: 150,
      //   Cell: (cell: any) => {
      //     return cell?.value ? (
      //       <CopyWrapper contentCopy={cell?.value} style={{minWidth: 140}}>
      //         {`${cell?.value}`}
      //       </CopyWrapper>
      //     ) : "-";
      //   },
      // },
      {
        Header: t("Experience"),
        accessor: "experience",
        thWidth: 150,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const status: any = cell?.value?.length > 0 ? ENUM_EXPERIENCE_USER.EXPERIENCED : ENUM_EXPERIENCE_USER.NON_EXPERIENCED;
          return (
            <div className="text-center" style={{ minWidth: 100 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_EXPERIENCE[status] || "secondary"
                  }`}
              >
                {t(
                  OPTION_EXPERIENCE?.find(
                    (item: any) => String(item?.value) === String(status)
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: `${t("Expect Salary")} + ${t("Type")}`,
        accessor: "expectedSalary",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 220,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          const currency = item?.currency === "KRW" ? "₩" : "$";
          const price = cell?.value;
          const isNegotiable = item?.salaryNegotiable;
          const type = item?.salaryType;

          return isNegotiable ? (
            <div>{t("Negotiable")}</div>
          ) : (
            <div className="cursor-pointer" style={{ minWidth: "180px" }}>
              {price ? `${formatNumber(price)} ${currency}/ ` : "-"}
              {
                t(
                  LIST_OPTION_SALARY_TYPE?.find(
                    (item: any) => String(item?.value) === String(type)
                  )?.label || ""
                )
              }
            </div>
          )
        },
      },
      {
        Header: t("Expect Start Date"),
        accessor: "preferredStartDate",
        filterable: true,
        sortable: false,
        thWidth: 140,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "", "YYYY-MM-DD");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              <span>
                <span>{date || ""}</span>
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Introduce Text"),
        accessor: "introduce",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 250,
        Cell: (cell: any) => {

          return cell?.value ? <div>
            <CopyWrapper contentCopy={cell?.value} style={{ minWidth: 220 }}>
              {`${cell?.value}`}
            </CopyWrapper>
          </div> : "-";
        },
      },
      {
        Header: t("Region"),
        accessor: "preferredAddress",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 150,
        Cell: (cell: any) => {
          const formatData = cell?.value?.filter((item: any) => (item?.region1 || item?.region2));
          return formatData?.length > 0 ? (
            <div className="d-flex flex-wrap align-items-center gap-1">
              {formatData?.map((item: any, index: number) => (
                <div
                  key={index}
                  className="d-flex align-items-center gap-1 bg-primary badge rounded-pill"
                >
                  {[item?.region1, item?.region2].filter(Boolean).join(" - ")}
                </div>
              ))}
            </div>
          ) : "-";
        },
      },
      {
        Header: t("Submmited at"),
        accessor: "createdAt",
        filterable: true,
        sortable: false,
        thWidth: 150,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updatedAt",
        filterable: true,
        sortable: false,
        thWidth: 110,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value || "");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Action"),
        thClass: "text-center whitespace-nowrap",
        thWidth: 100,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0"
              style={{ minWidth: "70px" }}
            >
              <TooltipCustom title={t("Detail User")} id={`detail-${item?.id}`}>
                <Link
                  className="btn btn-sm btn-soft-primary edit-item-btn"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setItemToEdit(item);
                  }}
                >
                  <i className="ri-eye-fill align-bottom"></i>
                </Link>
              </TooltipCustom>
              {/* <TooltipCustom
                title={t("Button Delete Operator")}
                id={`detail-cp-${item?.id}`}
              >
                <li className="list-inline-item" id={`detail-cp-${item?.id}`}>
                  <Link
                    className="btn btn-sm btn-soft-danger edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      console.log("e", e);
                      e.preventDefault();
                      handleConfirmDelete(item);
                    }}
                  >
                    <i className="ri-delete-bin-3-fill align-bottom"></i>
                  </Link>
                </li>
              </TooltipCustom> */}
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, application?.pagination?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };
  // Begin::Delete

  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await UserApi?.deleteUser(id);
      // console.log(response);
      if (response?.success) {
        setIsConfirmLoading((_prev) => false);
        setItemToDelete(false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  // End::Delete

  useEffect(() => {
    document.title = `${t("Applications")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => { }, []);

  const onCloseImageClick = () => {
    setLinkImage(null);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("Applications")}
            pageTitle={t("Home")}
          />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <div className="d-block d-xl-flex justify-content-between align-items-start">
                    <div
                      className="d-flex w-100 flex-wrap gap-3 gap-md-2 menu-card-statistics justify-content-start mb-3 mb-xl-2 position-relative"
                      style={{ maxWidth: "100%" }}
                    >
                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={application?.pagination?.total || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Applied")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={application?.summary?.totalApplied || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Viewed")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={application?.summary?.totalViewed || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Interview")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={application?.summary?.totalInterview || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Hired")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={application?.summary?.totalHired || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Rejected")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={application?.summary?.totalRejected || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      <Card
                        className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0"
                        style={{ width: "180px" }}
                      >
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total User Cancelled")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={application?.summary?.totalUserCancelled || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>

                    {/* <div className="col-sm-auto text-end">
                      <button
                        type="button"
                        className="btn btn-success d-md-block"
                        onClick={() => {
                          setIsOpen(true);
                          setItemToEdit(null);
                        }}
                      >
                        <i className="ri-add-fill align-bottom me-1"></i>{" "}
                        {t("Button Create Application")}
                      </button>
                    </div> */}
                  </div>
                </CardHeader>

                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Job Title")}`}
                          isShow={!!jobTitleSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Job Title")}...`}
                            value={jobTitleSearch}
                            onChange={(e) => setJobTitleSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Owner Post")} isShow={true}>
                          <DropdownAsyncUsers
                            name="user"
                            onChangeSelect={(event: any) => {
                              setUserIdSearch(event);
                            }}
                            isMulti={false}
                            placeholder={`${t("Select Owner Post")}...`}
                            isShowIconCheck={false}
                            isAllOption={true}
                            listCheckedUsers={userIdSearch}
                            defaultParams={{
                              type: "MANAGER"
                            }}
                            isClearable={false}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Full Name")}`}
                          isShow={!!nickNameSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Full Name")}...`}
                            value={nickNameSearch}
                            onChange={(e) => setNicknameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Email")} isShow={!!emailSearch}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Email")}...`}
                            value={emailSearch}
                            onChange={(e) => setEmailSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Phone Number")}`}
                          isShow={!!phoneNumberSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Phone Number")}...`}
                            value={phoneNumberSearch}
                            onChange={(e) =>
                              setPhoneNumberSearch(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Introduce")}`}
                          isShow={!!introduce}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Introduce")}...`}
                            value={introduce}
                            onChange={(e) => setIntroduce(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      {/* <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={`${t("Region")}`} isShow>
                          <DropdownOptionNew
                            name="region"
                            dataList={[]}
                            placeholder={`${t("Select Region")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={regionSearch}
                            onChangeSelect={(e: any) =>
                              setRegionSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                          />
                        </LabelWrapper>
                      </Col> */}

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={`${t("Experience")}`} isShow>
                          <DropdownStatusNew
                            name="experience"
                            dataList={OPTION_EXPERIENCE || []}
                            placeholder={`${t("Select Experience")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={experienceSearch}
                            onChangeSelect={(e: any) =>
                              setExperienceSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_EXPERIENCE}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={`${t("Gender")}`} isShow>
                          <DropdownOptionNew
                            name="gender"
                            dataList={OPTION_GENDER || []}
                            placeholder={`${t("Select Gender")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={genderSearch}
                            onChangeSelect={(e: any) =>
                              setGenderSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Application Status")}`}
                          isShow
                        >
                          <DropdownStatusNew
                            name="status"
                            dataList={LIST_APPLICATION_STATUS || []}
                            placeholder={`${t("Select Application Status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={statusSearch}
                            onChangeSelect={(e: any) =>
                              setStatusSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_APPLICATION_STATUS2}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={`${t("Salary Type")}`} isShow>
                          <DropdownOptionNew
                            name="status"
                            dataList={OPTION_SALARY_TYPE || []}
                            placeholder={`${t("Select Salary Type")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={salaryTypeSearch}
                            onChangeSelect={(e: any) =>
                              setSalaryTypeSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                          />
                        </LabelWrapper>
                      </Col>

                      {salaryTypeSearch !== "NEGOTIABLE" && (
                        <React.Fragment>
                          <Col
                            sm={12}
                            md={3}
                            xl={3}
                            xxl={3}
                            className="mt-3 mt-md-3"
                          >
                            <LabelWrapper
                              label={`${t("Salary From")}`}
                              isShow={!!salaryFromSearch}
                            >
                              <div className="d-flex gap-2 align-items-center">
                                <NumericFormat
                                  className="form-control search"
                                  value={salaryFromSearch}
                                  placeholder={t("Salary From")}
                                  allowLeadingZeros
                                  thousandSeparator=","
                                  isAllowed={(values) => {
                                    const { formattedValue, floatValue } = values;
                                    return formattedValue === "" || (floatValue || 0) <= 9999999999;
                                  }}
                                  onValueChange={(values: any) => {
                                    const { floatValue } = values;
                                    setSalaryFromSearch(floatValue)
                                  }}
                                />
                                <div>₩</div>
                              </div>
                            </LabelWrapper>
                          </Col>

                          <Col
                            sm={12}
                            md={3}
                            xl={3}
                            xxl={3}
                            className="mt-3 mt-md-3"
                          >
                            <LabelWrapper
                              label={`${t("Salary To")}`}
                              isShow={!!salaryToSearch}
                            >
                              <div className="d-flex gap-2 align-items-center">
                                <NumericFormat
                                  className="form-control search"
                                  value={salaryToSearch}
                                  placeholder={t("Salary To")}
                                  allowLeadingZeros
                                  thousandSeparator=","
                                  isAllowed={(values) => {
                                    const { formattedValue, floatValue } = values;
                                    return formattedValue === "" || (floatValue || 0) <= 9999999999;
                                  }}
                                  onValueChange={(values: any) => {
                                    const { floatValue } = values;
                                    setSalaryToSearch(floatValue)
                                  }}
                                />
                                <div>₩</div>
                              </div>
                            </LabelWrapper>
                          </Col>
                        </React.Fragment>
                      )}

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-sm-3 mt-md-3 mt-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!startDate || !!endDate}
                        >
                          <DatePickerCustom
                            startDate={startDate || null}
                            endDate={endDate || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={`${salaryTypeSearch !== "NEGOTIABLE" ? 9 : 3}`}
                        xl={`${salaryTypeSearch !== "NEGOTIABLE" ? 9 : 3}`}
                        xxl={`${salaryTypeSearch !== "NEGOTIABLE" ? 9 : 3}`}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-4 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed sticky-table-application-list"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={
                        application?.items?.length ? application?.items : []
                      }
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={application?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(application?.pagination?.total) /
                        Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sortBy: query.sortBy,
                        orderBy: query.orderBy,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isApplicationLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* <Modal
                    isOpen={!!itemToEdit?._id || isOpen}
                    centered={true}
                    size="xl"
                    scrollable={true}
                >
                    <ModalHeader toggle={onCloseClick}>
                        <>
                            {!!itemToEdit?._id
                                ? t("Button Update User")
                                : t("Button Create User")}
                        </>{" "}
                        <>
                            {itemToEdit?._id && (" - " + (itemToEdit?.fullname || "") || "")}
                        </>
                    </ModalHeader>
                    <ModalBody>
                        <div style={{ minHeight: "calc(100dvh - 200px)" }}>
                            <UserForm
                                isModal={true}
                                id={String(itemToEdit?._id || "")}
                                isCopy={!!itemToEdit?.isCopy}
                                triggerRefresh={triggerRefresh}
                                LIST_OPTION_ROLE={LIST_OPTION_ROLE_LANG}
                            />
                        </div>
                    </ModalBody>
                </Modal> */}

        <Detail
          isOpen={!!itemToEdit}
          item={itemToEdit}
          onClose={onCloseClick}
          triggerRefresh={triggerRefresh}
          setLinkImage={setLinkImage}
        />

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete User")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?._id)}
          />
        )}

        <ModalPreview
          type={"IMAGE"}
          isOpen={!!linkImage?.url}
          url={linkImage?.url}
          onClose={() => onCloseImageClick()}
          noImage={noImageJob}
        />

        <ModalMultiPreviewImage
          isOpen={!!linkImage?.urls}
          urls={linkImage?.urls || []}
          onClose={() => onCloseImageClick()}
          noImage={noImageJob}
        />
      </div>
    </React.Fragment>
  );
};

export default ApplicationManagement;
